import React, { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { DomainCheckoutData } from '../../pages/checkout/DomainCheckout';
import { orderService, CreateDomainOrderData } from '../../services/orderService';
import { stripePromise } from '../../config/stripe';
import StripePaymentForm from './StripePaymentForm';

interface DomainPaymentStepProps {
  data: DomainCheckoutData;
  onNext: (data: Partial<DomainCheckoutData>) => void;
  onBack: () => void;
}

const DomainPaymentStep: React.FC<DomainPaymentStepProps> = ({ data, onNext, onBack }) => {
  const [processing, setProcessing] = useState(false);
  // Only card payments are offered; PayPal was never implemented. Kept as a
  // constant so the existing `paymentMethod === 'card'` guards still read clearly.
  const paymentMethod: 'card' = 'card';
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [orderCreated, setOrderCreated] = useState<any>(null);

  const createOrder = async () => {
    if (orderCreated) return orderCreated; // Return existing order if already created

    // F4: Login is enforced upstream by DomainCheckout. This is a defensive
    // double-check; we don't silently auto-create accounts anymore.
    if (!localStorage.getItem('token')) {
      throw new Error('You must be signed in to complete checkout. Please log in and try again.');
    }

    try {
      // Prepare order data for domain-only purchase
      const orderData: CreateDomainOrderData = {
        domain_name: data.domainName,
        billing_cycle: data.billingCycle,
        contact_info: data.contactInfo,
        pricing_details: data.pricing,
        payment_method: 'stripe',
        total_amount: data.pricing.total,
        currency: 'USD',
        billing_address: data.contactInfo.address,
      };

      console.log('🛒 Creating authenticated domain order...');

      // F4: with login required, we use the authenticated domain-order
      // endpoint. The createGuestDomainOrder path is gone because guests
      // can no longer reach this step.
      const orderResult = await orderService.createDomainOrder(orderData);

      if (!orderResult.success) {
        throw new Error(orderResult.error || 'Failed to create order');
      }

      console.log('✅ Order created successfully:', orderResult.data);
      setOrderCreated(orderResult.data);
      return orderResult.data;
    } catch (error: any) {
      console.error('❌ Error creating order:', error);
      throw error;
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      console.log('✅ Payment completed successfully:', paymentIntentId);
      console.log('📋 Order details:', orderCreated);

      // Pass order data to next step - payment webhook will handle order status updates
      onNext({
        orderId: orderCreated.id,
        orderNumber: orderCreated.order_number,
        paymentIntentId: paymentIntentId,
      });

    } catch (error: any) {
      console.error('❌ Error processing payment success:', error);
      setErrors({ payment: error.message || 'Payment processing failed' });
    }
  };

  const handlePaymentError = (error: string) => {
    console.error('❌ Payment error:', error);
    setErrors({ payment: error });
    setProcessing(false);
  };

  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
      {/* Glossy overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
      
      <div className="max-w-2xl mx-auto relative z-10">
        {/* Badge */}
        <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
          <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
          <span className="text-sm font-semibold text-gray-700">Step 3: Payment</span>
        </div>
        
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
          Secure <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Payment</span>
        </h2>
      
      {/* Order Summary */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Order Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Domain Registration - {data.domainName}</span>
            <span className="font-medium">${data.pricing.domainPrice.toFixed(2)}</span>
          </div>
          {data.pricing.discount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discount</span>
              <span>-${data.pricing.discount.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t pt-2 flex justify-between font-bold text-lg">
            <span>Total</span>
            <span>${data.pricing.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Payment Method</h3>
        <div className="grid grid-cols-1 gap-4">
          <div
            className="border-2 rounded-lg p-4 border-secondary-500 bg-secondary-50"
          >
            <div className="flex items-center">
              <input
                type="radio"
                name="paymentMethod"
                value="card"
                checked
                readOnly
                className="mr-3"
              />
              <div>
                <div className="font-medium">Credit/Debit Card</div>
                <div className="text-sm text-gray-500">Visa, Mastercard, American Express, Discover</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          {Object.values(errors).map((error, index) => (
            <p key={index} className="text-red-700 text-sm">{error}</p>
          ))}
        </div>
      )}

      {/* Payment Form */}
      {paymentMethod === 'card' && !orderCreated && (
        <div className="mb-8">
          <div className="text-center">
            <button
              onClick={async () => {
                setProcessing(true);
                try {
                  await createOrder();
                } catch (error: any) {
                  setErrors({ payment: error.message || 'Failed to create order' });
                } finally {
                  setProcessing(false);
                }
              }}
              disabled={processing}
              className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-3 rounded-lg font-semibold hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group disabled:opacity-50"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
              <span className="relative z-10">{processing ? 'Creating Order...' : 'Continue to Payment'}</span>
            </button>
          </div>
        </div>
      )}

      {paymentMethod === 'card' && orderCreated && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>
          <Elements stripe={stripePromise}>
            <StripePaymentForm
              amount={data.pricing.total}
              orderId={orderCreated.id}
              customerEmail={data.contactInfo.email}
              customerName={`${data.contactInfo.firstName} ${data.contactInfo.lastName}`}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
              isProcessing={processing}
              setIsProcessing={setProcessing}
            />
          </Elements>
        </div>
      )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={onBack}
            className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 shadow-md disabled:opacity-50"
            disabled={processing}
          >
            Back to Review
          </button>
        </div>
      </div>
    </div>
  );
};

export default DomainPaymentStep;